// NEW_FILE_JUSTIFIED: Customer portal page listing own policy application drafts — new route /my-insurance/applications

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeleteConfirmDialog } from '@/components/insurance/DeleteConfirmDialog';
import { FileEdit, Pencil, Eye, ArrowLeft, Plus, Trash2 } from 'lucide-react';

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'rejected':
      return 'destructive';
    case 'under_review':
      return 'secondary';
    default:
      return 'outline';
  }
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function MyApplicationsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';
  const { currentOrg } = useOrgContext();
  const { currentCompany } = useCompanyContext();

  const effectiveOrgId = (currentOrg?._id ?? currentCompany?.orgId) as  // AS_JUSTIFIED: orgId from org or company context
    | Id<'organisations'>
    | undefined;

  const drafts = useQuery(
    api['insurance/policyApplications'].listDrafts,
    effectiveOrgId ? { orgId: effectiveOrgId } : 'skip'
  );

  const batchDelete = useMutation(api['insurance/policyApplications'].batchDeleteDrafts);

  const draftItems = (drafts ?? []).filter(d => d.status === 'draft');

  const onDelete = useCallback(
    async (ids: string[]) => {
      await batchDelete({
        policyIds: ids as Id<'insurancePolicies'>[], // AS_JUSTIFIED: ids come from draft._id which are Id<'insurancePolicies'>
      });
    },
    [batchDelete]
  );

  const {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useBatchSelection(draftItems, onDelete);

  if (drafts === undefined) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <Button variant="ghost" size="sm" className="mb-2" onClick={() => navigate(basePath)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">My Applications</h1>
          <p className="text-muted-foreground mt-1 text-sm">Your saved policy application drafts</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <Button variant="destructive" size="sm" onClick={() => setIsDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete ({selectedCount})
            </Button>
          )}
          <Button onClick={() => navigate(`${basePath}/apply/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            New Application
          </Button>
        </div>
      </div>

      {drafts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FileEdit className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground font-medium">No applications yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Start a policy application and save it as a draft to see it here
          </p>
          <Button className="mt-4" onClick={() => navigate(`${basePath}/apply/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            Apply for Policy
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {draftItems.length > 0 && (
                    <Checkbox
                      checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all drafts"
                    />
                  )}
                </TableHead>
                <TableHead>Application</TableHead>
                <TableHead>Insurance Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drafts.map(draft => {
                const canResume = draft.status === 'draft' || draft.status === 'rejected';
                const isDraft = draft.status === 'draft';
                return (
                  <TableRow key={draft._id}>
                    <TableCell>
                      {isDraft && (
                        <Checkbox
                          checked={selectedIds.has(draft._id)}
                          onCheckedChange={checked => handleSelectRow(draft._id, !!checked)}
                          aria-label={`Select ${draft.customerName || draft.policyNumber}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {draft.customerName || draft.policyNumber}
                    </TableCell>
                    <TableCell>{draft.insuranceTypeName ?? '\u2014'}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(draft.status)}>
                        {statusLabel(draft.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(draft.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => navigate(`${basePath}/apply/new?draftId=${draft._id}`)}
                      >
                        {canResume ? (
                          <>
                            <Pencil className="h-3.5 w-3.5" /> Resume
                          </>
                        ) : (
                          <>
                            <Eye className="h-3.5 w-3.5" /> View
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedCount}
        entityLabel="draft"
      />
    </div>
  );
}
